import { JSX, useState } from "react";
import { useTranslation } from "react-i18next";

import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import TextField from "@mui/material/TextField";

import FeedbackButton from "lib/components/button/FeedbackButton";

import { asTitle } from "utils/Utils";

import UserRightsTable from "core/blueprint/base/access/UserAccessTable";
import {
	BlueprintInfo,
	updateBlueprintInfo,
	updateBlueprintRights,
	UserRights
} from "core/blueprint/base/BlueprintService";

export type EditBlueprintDialogProps = {
	info: BlueprintInfo;
	userRights: UserRights[];

	onClose: () => void;
	onConfirm: () => void;
};

export default function EditBlueprintDialog(
	props: Readonly<EditBlueprintDialogProps>
): JSX.Element {
	const { t } = useTranslation();

	const [name, setName] = useState(props.info.name);
	const [description, setDescription] = useState(props.info.description);
	const [accesses, setAccesses] = useState(props.userRights);
	const [accessesValid, setAccessesValid] = useState(true);

	// The name can not be empty!
	function nameIsInvalid() {
		return name.trim().length === 0;
	}

	function onSave(): Promise<void> {
		return updateBlueprintInfo(props.info.id, {
			name: name,
			description: description
		})
			.then(() => updateBlueprintRights(props.info.id, accesses))
			.then(() => props.onConfirm());
	}

	return (
		<Dialog
			open={true}
			onClick={e => e.stopPropagation()}
			onClose={props.onClose}>
			<DialogTitle>{asTitle(t("editBlueprint"))}</DialogTitle>
			<DialogContent>
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<TextField
							id="name"
							type="text"
							label={asTitle(t("name"))}
							error={nameIsInvalid()}
							required
							fullWidth
							inputProps={{ maxLength: 255 }}
							helperText={name.length.toString() + "/255"}
							value={name}
							onChange={e => setName(e.target.value)}
							variant="standard"
						/>
					</Grid>
					<Grid item xs={12}>
						<TextField
							id="description"
							type="text"
							label={asTitle(t("description"))}
							fullWidth
							multiline
							rows={3}
							inputProps={{ maxLength: 1000 }}
							helperText={description.length.toString() + "/1000"}
							value={description}
							onChange={e => setDescription(e.target.value)}
							variant="standard"
						/>
					</Grid>
					<Grid item xs={12}>
						<InputLabel id="access">
							{asTitle(t("access"))}
						</InputLabel>
						<UserRightsTable
							accesses={accesses}
							onChange={setAccesses}
							isValid={setAccessesValid}
						/>
					</Grid>
				</Grid>
				<DialogActions>
					<Button variant="outlined" onClick={props.onClose}>
						{asTitle(t("cancel"))}
					</Button>
					<FeedbackButton
						variant="contained"
						label={asTitle(t("save"))}
						disabled={nameIsInvalid() || !accessesValid}
						onClick={onSave}
					/>
				</DialogActions>
			</DialogContent>
		</Dialog>
	);
}
